import { Tooltip, useTheme } from '@flik/shared/components';
import React, { useEffect, useState } from 'react';
import { useUserData } from '../hooks/useUserData';
import FlikIcon from '../icons/FlikIcon';
interface Identity {
  id: string;
  type: 'User' | 'System' | 'Unknown';
}

interface UserCircularAvatarProps {
  identity: string | Identity;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  showLoading?: boolean;
  tooltipPosition?: 'left' | 'up' | 'right' | 'down' | 'center';
}

const UserCircularAvatar: React.FC<UserCircularAvatarProps> = ({
  identity,
  size = 'md',
  className = '',
  showLoading = true,
  tooltipPosition,
}) => {
  const { provideTailwind } = useTheme();
  const {
    getUserDataByIdentity,
    getUserDisplayName,
    getUserInitials,
    isUserDataCached,
  } = useUserData();
  const [participantInfo, setParticipantInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Extract identity ID and type
  const identityId = typeof identity === 'string' ? identity : identity.id;
  const identityType = typeof identity === 'string' ? 'User' : identity.type;

  // Check if this is the Flik AI system
  const isFlikAI = identityType === 'System' && identityId === 'flikai';

  // Load user data when identity changes (skip for Flik AI)
  useEffect(() => {
    if (identityId && !isFlikAI) {
      // Check if we have cached data first
      if (isUserDataCached(identityId)) {
        const cachedData = getUserDataByIdentity(identityId);
        if (cachedData) {
          setParticipantInfo(cachedData);
          setIsLoading(false);
          return;
        }
      }

      // Fetch user data if not cached
      setIsLoading(true);
      setParticipantInfo(null);

      getUserDataByIdentity(identityId)
        .then((data: any) => {
          setParticipantInfo(data);
          setIsLoading(false);
        })
        .catch((error: any) => {
          console.error('Failed to fetch participant info for avatar:', error);
          setIsLoading(false);
        });
    }
  }, [identityId, isFlikAI]);

  // Helper function to get initials from user info
  const getInitials = (): string => {
    // Special case for Flik AI
    if (isFlikAI) {
      return ''; // Will be replaced with icon
    }

    if (participantInfo) {
      const { firstName, lastName } = participantInfo;
      if (firstName && lastName) {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
      } else if (firstName) {
        return firstName.charAt(0).toUpperCase();
      } else if (lastName) {
        return lastName.charAt(0).toUpperCase();
      }
    }

    // Fallback to cached data or default
    return getUserInitials(identityId);
  };

  // Helper function to get display name for tooltip
  const getDisplayName = (): string => {
    // Special case for Flik AI
    if (isFlikAI) {
      return 'Flik AI';
    }

    if (participantInfo) {
      const { firstName, lastName } = participantInfo;
      if (firstName && lastName) {
        return `${firstName} ${lastName}`;
      } else if (firstName) {
        return firstName;
      } else if (lastName) {
        return lastName;
      }
    }

    // Fallback to cached data or identity
    return getUserDisplayName(identityId);
  };

  // Size classes
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-12 h-12 text-lg',
    lg: 'w-16 h-16 text-xl',
    xl: 'w-20 h-20 text-2xl',
  };

  // Get icon size based on avatar size
  const getIconSize = () => {
    switch (size) {
      case 'sm':
        return 'w-5 h-5';
      case 'md':
        return 'w-7 h-7';
      case 'lg':
        return 'w-9 h-9';
      case 'xl':
        return 'w-12 h-12';
      default:
        return 'w-7 h-7';
    }
  };

  const avatarContent = (
    <div
      className={`${
        sizeClasses[size]
      } rounded-full flex items-center justify-center ${provideTailwind(
        'bg-secondary',
      )} border-2 ${provideTailwind('border-white')} shadow-lg ${className}`}
    >
      {isLoading && showLoading ? (
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      ) : isFlikAI ? (
        <FlikIcon className={getIconSize()} color="black" />
      ) : (
        <span className={`${provideTailwind('text-primary')} font-semibold`}>
          {getInitials()}
        </span>
      )}
    </div>
  );

  // If tooltip position is provided, wrap with Tooltip component
  if (tooltipPosition) {
    return (
      <Tooltip content={getDisplayName()} direction={tooltipPosition}>
        {avatarContent}
      </Tooltip>
    );
  }

  // Otherwise, return just the avatar
  return avatarContent;
};

export default UserCircularAvatar;
